import React, {useEffect, useMemo, useState} from 'react';
import {PlusIcon} from "@heroicons/react/24/solid";
import Loader from "../ui/Loader";
import AccountItem from "./AccountItem";
import NewAccountForm from "./NewAccountForm";
import {accountApi} from "../../api/AccountsService";
import Filter from "../ui/Filter";
import {classNames} from "../../utils/common";
import {IAccount} from "../../model/IAccount";

type AccountSortBy = 'status' | 'currency';

const AccountsTable = () => {
    const {data: accounts, isLoading, error} = accountApi.useFetchAllAccountsQuery();
    const [filter, setFilter] = useState('');
    const [showNewAccountForm, setShowNewAccountForm] = useState(false);
    const [sortBy, setSortBy] = useState<AccountSortBy>('status');
    const [highlightedAccountId, setHighlightedAccountId] = useState<number | null>(null);

    const filteredAccounts = useMemo(() => {
        if (!accounts) {
            return [];
        }

        const filterInUpper = filter.toUpperCase();
        const filtered = filter === ''
            ? [...accounts]
            : accounts.filter((account) => {
                const statusLabel = account.is_active ? 'ACTIVE' : 'DISABLED';
                return account.name.toUpperCase().includes(filterInUpper)
                    || account.currency.symbol.toUpperCase().includes(filterInUpper)
                    || account.currency.code.toUpperCase().includes(filterInUpper)
                    || statusLabel.includes(filterInUpper);
            });

        return filtered.sort((a: IAccount, b: IAccount) => {
            if (sortBy === 'status') {
                if (a.is_active !== b.is_active) {
                    return a.is_active ? -1 : 1;
                }
                return a.currency.id - b.currency.id;
            }
            return a.currency.id - b.currency.id;
        });
    }, [accounts, filter, sortBy]);

    useEffect(() => {
        if (highlightedAccountId == null) {
            return;
        }

        const frame = window.requestAnimationFrame(() => {
            const row = document.querySelector(`[data-account-id="${highlightedAccountId}"]`) as HTMLElement | null;
            if (!row) {
                return;
            }
            row.scrollIntoView({behavior: 'smooth', block: 'nearest'});
            row.focus({preventScroll: true});
        });

        return () => {
            window.cancelAnimationFrame(frame);
        };
    }, [highlightedAccountId, filteredAccounts]);

    function handleAccountCreated(account: IAccount) {
        setHighlightedAccountId(account.id);
    }

    return (
        <>
            <div className="bg-white px-3 py-2 flex items-center border-t border-gray-200 sm:px-4">
                <div className="flex items-center">
                    <p className="text-xs text-gray-700 mr-2">
                        Filter
                    </p>
                    <Filter filter={filter} setFilter={setFilter} withLabel={false} wideField={false} smallerSize/>
                </div>

                <div className="flex items-center ml-6">
                    <p className="text-xs text-gray-700 mr-2">
                        Sorting
                    </p>
                    <div className="inline-flex" role="radiogroup" aria-label="Sorting">
                        <button
                            type="button"
                            role="radio"
                            aria-checked={sortBy === 'status'}
                            onClick={() => setSortBy('status')}
                            className={classNames(
                                sortBy === 'status'
                                    ? "bg-gray-300 text-indigo-900"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                                "px-3 py-1 text-xs font-medium rounded-l-full focus:outline-none"
                            )}
                        >
                            status
                        </button>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={sortBy === 'currency'}
                            onClick={() => setSortBy('currency')}
                            className={classNames(
                                sortBy === 'currency'
                                    ? "bg-gray-300 text-indigo-900"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                                "px-3 py-1 text-xs font-medium rounded-r-full focus:outline-none"
                            )}
                        >
                            currency
                        </button>
                    </div>
                </div>

                <button
                    type="button"
                    title="Add account"
                    aria-label="Add account"
                    onClick={() => setShowNewAccountForm((visible) => !visible)}
                    className={classNames(
                        showNewAccountForm
                            ? "bg-green-200 text-green-900"
                            : "bg-green-100 text-green-800 hover:bg-green-200",
                        "ml-auto inline-flex items-center justify-center rounded-full p-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                    )}
                >
                    <PlusIcon className="h-4 w-4"/>
                </button>
            </div>
            <NewAccountForm
                show={showNewAccountForm}
                onClose={() => setShowNewAccountForm(false)}
                onCreated={handleAccountCreated}
            />
            {isLoading && <div className="flex justify-center"><Loader></Loader></div>}
            {error && <div>Error occurred during fetching Accounts: {error.toString()}</div>}
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Account
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Currency
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Initial Balance
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Balance
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                    </th>
                    {/* Toggle status */}
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"/>
                    {/* Additional menu */}
                    <th scope="col" className="relative px-3 py-2">
                        <span className="sr-only">Actions</span>
                    </th>
                </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {filteredAccounts.map((account) =>
                    <AccountItem
                        key={account.id}
                        account={account}
                        highlighted={highlightedAccountId === account.id}
                        onHighlight={setHighlightedAccountId}
                    />
                )}
                </tbody>
            </table>
            {filteredAccounts.length === 0 && !isLoading && (
                <div className="flex justify-center text-base text-gray-700 py-3">No accounts</div>
            )}
        </>
    );
};

export default AccountsTable;
